import { createClientRequestId } from "@/lib/idempotency";

export type PayloadIdempotencyAttempt = {
  key: string;
  fingerprint: string;
  completedAt?: number;
};

const completedReplayWindowMs = 15_000;

function storageKey(scope: string) {
  return `prvbuy:idempotency-attempt:${scope}`;
}

function readStoredAttempt(scope: string) {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(storageKey(scope)) ?? window.sessionStorage.getItem(storageKey(scope));
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<PayloadIdempotencyAttempt>;
    if (typeof parsed.completedAt === "number" && Date.now() - parsed.completedAt > completedReplayWindowMs) {
      window.localStorage.removeItem(storageKey(scope));
      window.sessionStorage.removeItem(storageKey(scope));
      return null;
    }
    return typeof parsed.key === "string" && typeof parsed.fingerprint === "string"
      ? { key: parsed.key, fingerprint: parsed.fingerprint, completedAt: parsed.completedAt }
      : null;
  } catch {
    return null;
  }
}

function storeAttempt(scope: string, attempt: PayloadIdempotencyAttempt) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify(attempt));
    window.sessionStorage.removeItem(storageKey(scope));
  } catch {
    // The in-memory ref retained by the caller still protects retries in this page lifetime.
  }
}

export function getPayloadIdempotencyAttempt(
  scope: string,
  payload: unknown,
  current: PayloadIdempotencyAttempt | null,
) {
  const fingerprint = JSON.stringify(payload ?? null);
  if (current?.fingerprint === fingerprint) return current;

  const stored = readStoredAttempt(scope);
  if (stored?.fingerprint === fingerprint) return stored;

  const attempt = { key: createClientRequestId(), fingerprint };
  storeAttempt(scope, attempt);
  return attempt;
}

export function completePayloadIdempotencyAttempt(scope: string, attempt: PayloadIdempotencyAttempt) {
  if (typeof window === "undefined") return;
  try {
    const stored = readStoredAttempt(scope);
    if (stored?.key === attempt.key) storeAttempt(scope, { ...attempt, completedAt: Date.now() });
  } catch {
    // Completion is already represented by clearing the caller's in-memory ref.
  }
}
